/**
 * AP-39 12.1 — **Zähler und Ablage des Posteingangs**, geteilt vom Menükopf, vom
 * Menüpunkt und der Seite `/inbox`. Liegt in `components/`, weil das Layout ihn
 * braucht und das Layout kein Feature importiert.
 *
 * Eine Anfrage `GET /inbox/count` beim Laden, alle 60 Sekunden, wenn das Fenster
 * den Fokus bekommt, und sofort nach eigenen Aktionen (`zaehlerNeu()`).
 */
import { create } from 'zustand'
import { erfuelltRecht, type InboxCounter } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'

export interface AbgelegterEintrag {
  id: string
  fileName: string
  status: string
  error: string | null
}

interface PosteingangState {
  zaehler: InboxCounter | null
  /** Rückmeldung nach dem Ablegen außerhalb der Seite (Menükopf, Menüpunkt) */
  hinweis: { text: string; fehler: boolean } | null
  /** Hochgezählt nach jedem Ablegen — die Seite lädt daran neu */
  stand: number
  zaehlerNeu: () => Promise<void>
  ablegen: (dateien: File[]) => Promise<AbgelegterEintrag[]>
  hinweisWeg: () => void
}

/** Sieht dieser Benutzer den Posteingang? Dieselbe Frage wie Menüpunkt und Route. */
export function darfPosteingang(): boolean {
  const { user, hasModule } = useAuthStore.getState()
  return hasModule('INBOX.CORE') && erfuelltRecht('inbox.item.read', user?.permissions)
}

export function darfAblegen(): boolean {
  const { user } = useAuthStore.getState()
  return darfPosteingang() && erfuelltRecht('inbox.item.manage', user?.permissions)
}

/** „3 Dokumente abgelegt, 1 nicht angenommen" — ein Satz für den Hinweis. */
export function ablageText(e: AbgelegterEintrag[]): string {
  const fehler = e.filter((x) => x.status === 'FAILED')
  const dubletten = e.filter((x) => x.status === 'DUPLICATE')
  const gut = e.length - fehler.length - dubletten.length
  const teile = [
    `${gut} ${gut === 1 ? 'Dokument' : 'Dokumente'} im Posteingang abgelegt`,
    dubletten.length ? `${dubletten.length} schon vorhanden` : '',
    fehler.length ? `nicht angenommen: ${fehler.map((x) => x.fileName).join(', ')}` : '',
  ]
  return teile.filter(Boolean).join(' · ')
}

export const usePosteingang = create<PosteingangState>((set, get) => ({
  zaehler: null,
  hinweis: null,
  stand: 0,
  zaehlerNeu: async () => {
    if (!darfPosteingang()) {
      if (get().zaehler) set({ zaehler: null })
      return
    }
    try {
      const r = await api.get<{ data: InboxCounter }>('/inbox/count')
      set({ zaehler: r.data.data })
    } catch {
      // Der Zähler ist Beiwerk — ein Fehler hier darf keine Seite stören.
    }
  },
  ablegen: async (dateien) => {
    const form = new FormData()
    for (const d of dateien) form.append('files', d, d.name)
    try {
      const r = await api.post<{ data: AbgelegterEintrag[] }>('/inbox/items', form)
      set((s) => ({ stand: s.stand + 1, hinweis: { text: ablageText(r.data.data), fehler: r.data.data.some((x) => x.status === 'FAILED') } }))
      void get().zaehlerNeu()
      return r.data.data
    } catch (e) {
      set({ hinweis: { text: apiErrorText(e, 'Ablegen hat nicht geklappt.'), fehler: true } })
      return []
    }
  },
  hinweisWeg: () => set({ hinweis: null }),
}))

/** Enthält das Ziehen Dateien? Text oder Verweise nimmt der Posteingang nicht an. */
export function ziehtDateien(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files')
}

/**
 * Eigenschaften für eine Ablagefläche (5.1, PE-5): Symbol im Menükopf, Menüpunkt, Seite.
 * `aktiv` zeigt, dass gerade Dateien darüber gezogen werden.
 */
export function ablageflaeche(setAktiv: (a: boolean) => void, ablegen: (d: File[]) => void) {
  return {
    onDragOver: (e: React.DragEvent) => {
      if (!ziehtDateien(e)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
      setAktiv(true)
    },
    onDragLeave: (e: React.DragEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
      setAktiv(false)
    },
    onDrop: (e: React.DragEvent) => {
      if (!ziehtDateien(e)) return
      e.preventDefault()
      e.stopPropagation()
      setAktiv(false)
      const dateien = Array.from(e.dataTransfer.files)
      if (dateien.length) ablegen(dateien)
    },
  }
}
